'use client'

import { BuilderBlocks } from '@builder.io/react'
import { type BuilderBlock } from '@builder.io/react/dist/types/src/components/builder-block.component'
import Link from 'next/link'
import React from 'react'

import { Container, type ContainerProps } from '@src/components/Container/Container'
import { useBreakpoints } from '@src/utils/breakpoints.util'
import { cn } from '@src/utils/tailwind'

interface ResponsiveValue {
  desktop: number
  mobile: number
  tablet: number
}

interface GridProps {
  columns: any[]
  space: number
  responsiveColumns: ResponsiveValue
  responsiveSpace: ResponsiveValue
  containerWrapper: boolean
  builderBlock: BuilderBlock
  attributes: any
  alignItems: 'center' | 'left' | 'right'
  innerSpacing: ContainerProps['innerSpacing']
  outerSpacing: ContainerProps['outerSpacing']
}

export const Grid = ({
  columns = [],
  responsiveColumns,
  space,
  responsiveSpace,
  containerWrapper,
  builderBlock,
  attributes,
  alignItems,
  innerSpacing,
  outerSpacing,
}: GridProps) => {
  const { isMobile, isTablet } = useBreakpoints()

  const Wrapper = containerWrapper ? Container : 'div'
  const wrapperProps = containerWrapper ? { innerSpacing, outerSpacing } : {}
  const { style, key, ...filteredAttributes } = attributes
  // eslint-disable-next-line tailwindcss/no-custom-classname
  const flexClassName = cn(
    `lg:grid-cols-${responsiveColumns.desktop} md:grid-cols-${responsiveColumns.tablet} sm:grid-cols-${responsiveColumns.mobile}`,
    {
      'builder-columns w-full': true,
      grid: alignItems !== 'center',
      'flex flex-wrap items-center justify-center': alignItems === 'center',
    }
  )
  const flexBasis = `basis-[${100 / columns.length}]`
  const columnClassName = cn({
    'builder-column flex w-full flex-row': alignItems !== 'center',
    'lg:flex lg:grow-1 lg:shrink-0': alignItems === 'center',
    [flexBasis]: alignItems === 'center',
  })
  const direction: any = {
    left: 'ltr',
    right: 'rtl',
    center: 'ltr',
  }

  const gap =
    responsiveSpace?.desktop || responsiveSpace?.tablet || responsiveSpace?.mobile
      ? isMobile
        ? responsiveSpace?.mobile
        : isTablet
          ? responsiveSpace?.tablet
          : responsiveSpace?.desktop
      : space

  return (
    <Wrapper key={key} {...filteredAttributes} {...wrapperProps}>
      <div
        className={flexClassName}
        // property direction for grid is not included on tailwind
        style={{ gap: `${gap}px`, direction: direction[alignItems] }}
      >
        {columns.map((col: { link: any; blocks: any }, index: number) => {
          const TagName = col.link ? Link : 'div'
          return (
            <React.Fragment key={index}>
              {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing */}
              <TagName className={columnClassName} href={col.link || '#'}>
                <BuilderBlocks
                  key={index}
                  className="w-full"
                  fieldName="Block"
                  parentElementId={builderBlock?.id}
                  blocks={col.blocks}
                  dataPath={`component.options.columns.${index}.blocks`}
                />
              </TagName>
            </React.Fragment>
          )
        })}
      </div>
    </Wrapper>
  )
}
